import React from 'react';
import Button from '@/components/Button/Button';

export interface ProgressBarProps {
  /** Completion percentage from 0–100 */
  value: number;
  id?: string;
  className?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ value, id, className }) => {
  const pct = Math.min(100, Math.max(0, Math.round(value)));

  return (
    <div
      id={id}
      className={`task-progress-bar ${className ?? ''}`.trim()}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="task-progress-bar__track">
        <div className="task-progress-bar__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export interface StickyTasklistDocBarProps {
  idPrefix?: string;
  completedCount: number;
  totalCount: number;
  /** User has confirmed task / page complete (progress meter hidden while true). */
  taskFinished: boolean;
  /** e.g. “Mark Read” pages: hide progress block. */
  omitProgress?: boolean;
  /** `true` = task-style copy (“Mark Task Complete” / “Task complete!”); `false` = module/read copy. */
  isAction?: boolean;
  allChecked: boolean;
  onMarkComplete?: () => void;
  onMarkIncomplete?: () => void;
  /** When not all subtasks are done, outline CTA (e.g. jump to What To Do Next). */
  onJumpToNext?: () => void;
  jumpToNextLabel?: string;
  markCompleteLabel?: string;
  completedPillLabel?: string;
  markIncompleteLabel?: string;
  markCompletePending?: boolean;
  allowMarkIncomplete?: boolean;
  progressLabelLeft?: string;
}

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="14"
    height="11"
    viewBox="0 0 14 11"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12.25 1.625L5.25 8.625L1.75 5.125"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Fixed bottom bar for checklist JSON pages: progress + CTA, or completion pill + unmark.
 * Layout: bar capped at 80% width and centered to avoid overlapping the chat FAB;
 * desktop (lg+) floating pill offset for sidebar.
 */
export const StickyTasklistDocBar: React.FC<StickyTasklistDocBarProps> = ({
  idPrefix = 'sticky-doc-bar',
  completedCount,
  totalCount,
  taskFinished,
  omitProgress = false,
  isAction = true,
  allChecked,
  onMarkComplete,
  onMarkIncomplete,
  onJumpToNext,
  jumpToNextLabel = 'What To Do Next',
  markCompleteLabel,
  completedPillLabel,
  markIncompleteLabel = 'Mark as incomplete',
  markCompletePending = false,
  allowMarkIncomplete = false,
  progressLabelLeft = 'Progress',
}) => {
  const progressId = `${idPrefix}-progress`;
  const progressLabelId = `${idPrefix}-progress-label`;
  const completedSummary = totalCount === 0 ? '—' : `${completedCount} of ${totalCount} completed`;
  const progressPct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const hasProgress = totalCount > 0 && !taskFinished && !omitProgress;
  const ctaLabel = markCompleteLabel ?? (isAction ? 'Mark Task Complete' : 'Mark Read');
  const pillLabel =
    completedPillLabel ?? (isAction ? 'Task complete!' : 'Module complete!');

  const markDisabled =
    markCompletePending || (!allowMarkIncomplete && !allChecked) || totalCount === 0;

  const statusMsgId = `${idPrefix}-status-msg`;
  const labelledBy = taskFinished ? statusMsgId : hasProgress ? progressLabelId : undefined;

  return (
    <section
      className="sticky-checklist-doc-bar"
      {...(labelledBy
        ? { 'aria-labelledby': labelledBy }
        : { 'aria-label': isAction ? 'Task progress' : 'Document read status' })}
    >
      <div className="sticky-checklist-doc-bar__viewport">
        <div className="sticky-checklist-doc-bar__max">
          <div
            className={[
              'sticky-checklist-doc-bar__card',
              hasProgress ? '' : 'sticky-checklist-doc-bar__card--center-actions',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {hasProgress ? (
              <div className="sticky-checklist-doc-bar__meter">
                <div className="sticky-checklist-doc-bar__meter-labels">
                  <span className="sticky-checklist-doc-bar__meter-left" id={progressLabelId}>
                    {progressLabelLeft}
                  </span>
                  <span className="sticky-checklist-doc-bar__meter-right">{completedSummary}</span>
                </div>
                <ProgressBar
                  id={progressId}
                  value={progressPct}
                  className="sticky-checklist-doc-bar__progress task-progress-bar--checklist-footer"
                />
              </div>
            ) : null}

            {taskFinished ? (
              <div className="sticky-checklist-doc-bar__complete-row">
                <div className="sticky-checklist-doc-bar__complete-pill" id={statusMsgId} role="status" aria-live="polite">
                  <span className="sticky-checklist-doc-bar__complete-icon" aria-hidden="true">
                    <CheckIcon className="sticky-checklist-doc-bar__complete-check" />
                  </span>
                  <span className="sticky-checklist-doc-bar__complete-title">{pillLabel}</span>
                </div>
                {onMarkIncomplete ? (
                  <button
                    type="button"
                    className="sticky-checklist-doc-bar__unmark"
                    onClick={onMarkIncomplete}
                  >
                    {markIncompleteLabel}
                  </button>
                ) : null}
              </div>
            ) : (
              <div
                className={[
                  'sticky-checklist-doc-bar__cta-wrap',
                  hasProgress ? 'sticky-checklist-doc-bar__cta-wrap--push-end' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {allChecked && onMarkComplete ? (
                  <Button
                    type="button"
                    size="lg"
                    variant="fill"
                    color="primary"
                    label={ctaLabel}
                    onClick={onMarkComplete}
                    disabled={markDisabled}
                    width="fill"
                    className="sticky-checklist-doc-bar__cta-btn"
                  />
                ) : onJumpToNext ? (
                  <Button
                    type="button"
                    size="lg"
                    variant="border"
                    color="primary"
                    label={jumpToNextLabel}
                    onClick={onJumpToNext}
                    width="fill"
                    className="sticky-checklist-doc-bar__cta-btn"
                  />
                ) : onMarkComplete ? (
                  <Button
                    type="button"
                    size="lg"
                    variant="fill"
                    color="primary"
                    label={ctaLabel}
                    onClick={onMarkComplete}
                    disabled={markDisabled}
                    width="fill"
                    className="sticky-checklist-doc-bar__cta-btn"
                  />
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProgressBar;
